import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../../services/apiClient';
import logo from '../../assets/logo-badge.webp';

// Página pública para FIJAR contraseña desde un enlace. Acepta el token como path
// param (compat: /activar/:token, /recuperar/:token) o como query (?token=…), que es
// el formato que arma el correo del núcleo: /activar?token=… y /restablecer?token=….
// Valida el token (GET) y al guardar hace set-password + auto-login (recarga a "/").
type TokenInfo = { valid: true; type: 'invite' | 'reset'; firstname: string; username: string; has_email: boolean };

export default function SetPasswordPage({ mode }: { mode: 'invite' | 'reset' }) {
  const { token: pathToken = '' } = useParams();
  const token = pathToken || new URLSearchParams(window.location.search).get('token') || '';
  const [info, setInfo] = useState<TokenInfo | null>(null);
  const [checking, setChecking] = useState(true);
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    // Núcleo: GET /api/auth/token/:kind/:token → { valid, name, email }.
    api.get<{ valid: boolean; name?: string; email?: string }>(`/auth/token/${mode}/${token}`)
      .then((r) => {
        if (!alive) return;
        setInfo(r && r.valid
          ? { valid: true, type: mode, firstname: r.name || '', username: r.email || '', has_email: !!r.email }
          : null);
      })
      .catch(() => { if (alive) setInfo(null); })
      .finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [token, mode]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (pass.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (pass !== pass2) { setError('Las contraseñas no coinciden.'); return; }
    setBusy(true);
    try {
      // Núcleo: 'invite' consume por /auth/activar; 'reset' por /auth/reset (ambos auto-login).
      await api.post(`/auth/${mode === 'invite' ? 'activar' : 'reset'}`, { token, password: pass });
      // La sesión ya quedó activa (cookie). Recarga completa → AuthProvider carga el portal.
      window.location.href = '/';
    } catch (err) {
      const e = err as ApiError;
      setError(e?.message || 'No se pudo guardar la contraseña.');
      setBusy(false);
    }
  }

  const titulo = mode === 'invite' ? 'Crea tu contraseña' : 'Restablece tu contraseña';

  return (
    <div className="force-light relative flex min-h-full items-center justify-center overflow-hidden bg-canvas p-4 text-content">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-accent/30 blur-3xl" />
      <div className="relative w-full max-w-sm rounded-2xl border border-line bg-surface p-8 shadow-xl">
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 -m-1 rounded-full bg-accent/40 blur-md" />
            <img src={logo} alt="Let's Move Dance & Fitness" className="relative h-24 w-24 rounded-full bg-white object-contain shadow-md ring-4 ring-accent/60" />
          </div>
          <div className="text-center">
            <h1 className="font-display text-2xl font-extrabold tracking-wide text-content">LET'S MOVE</h1>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-content-3">Dance &amp; Fitness</p>
          </div>
        </div>

        {checking ? (
          <p className="py-6 text-center text-sm text-content-3">Verificando enlace…</p>
        ) : !info ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-5 text-center text-sm text-red-700">
            Este enlace no es válido o ya expiró.
            <div className="mt-3">
              <a href="/recuperar" className="font-semibold text-brand-blue underline">Solicitar uno nuevo</a>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <p className="mb-4 text-center text-sm text-content-2">
              ¡Hola <span className="font-semibold text-content">{info.firstname}</span>! {titulo.toLowerCase()} para entrar a tu portal.
            </p>
            <div className="mb-4 rounded-lg bg-surface-2 px-3 py-2 text-center text-sm">
              <span className="text-content-3">Tu usuario:</span> <span className="font-semibold text-content">{info.username}</span>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}

            <label className="mb-1 block text-sm font-medium text-content-2">Nueva contraseña</label>
            <input type="password" autoFocus value={pass} onChange={(e) => setPass(e.target.value)}
              className="mb-4 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30"
              placeholder="Mínimo 8 caracteres" />

            <label className="mb-1 block text-sm font-medium text-content-2">Repite la contraseña</label>
            <input type="password" value={pass2} onChange={(e) => setPass2(e.target.value)}
              className="mb-6 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30"
              placeholder="••••••••" />

            <button type="submit" disabled={busy || !pass || !pass2}
              className="w-full rounded-lg bg-accent-solid py-2.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:cursor-not-allowed disabled:bg-line disabled:text-content-3">
              {busy ? 'Guardando…' : 'Guardar y entrar'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
